import { useEffect, useState } from "react";
import { fetchRequests, decideRequest, type TimeOffRequest } from "../lib/api";
import RequestTable from "../components/RequestTable";
import DenyDialog from "../components/DenyDialog";

export default function Approvals() {
  const [requests, setRequests] = useState<TimeOffRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [denying, setDenying] = useState<TimeOffRequest | null>(null);

  function reload() {
    fetchRequests("team")
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load requests"));
  }

  useEffect(reload, []);

  async function handleApprove(id: string) {
    setBusyId(id);
    try {
      const updated = await decideRequest(id, "approve");
      setRequests((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to acknowledge request");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDenyConfirm(reason: string) {
    if (!denying) return;
    setBusyId(denying.id);
    try {
      const updated = await decideRequest(denying.id, "deny", reason);
      setRequests((prev) => prev?.map((r) => (r.id === denying.id ? updated : r)) ?? null);
      setDenying(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send this back for coordination");
    } finally {
      setBusyId(null);
    }
  }

  const pending = requests?.filter((r) => r.status === "pending") ?? [];
  const decided = requests?.filter((r) => r.status !== "pending") ?? [];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="di-h3">Acknowledgements</h1>
      </div>

      {error && <p className="form-error">{error}</p>}

      {requests === null ? (
        <p className="di-small">Loading…</p>
      ) : (
        <>
          <section className="di-card">
            <h2 className="di-h4">Awaiting your decision</h2>
            <RequestTable
              requests={pending}
              showPerson
              emptyMessage="Nothing waiting on you right now."
              renderActions={(r) => (
                <div className="action-group">
                  <button
                    className="di-btn di-btn-primary di-btn-small"
                    disabled={busyId === r.id}
                    onClick={() => handleApprove(r.id)}
                  >
                    Acknowledge
                  </button>
                  <button
                    className="di-btn di-btn-outline di-btn-small"
                    disabled={busyId === r.id}
                    onClick={() => setDenying(r)}
                  >
                    Needs Coordination
                  </button>
                </div>
              )}
            />
          </section>

          <section className="di-card">
            <h2 className="di-h4">Past decisions</h2>
            <RequestTable requests={decided} showPerson showDecidedAt emptyMessage="No past requests from your team yet." />
          </section>
        </>
      )}

      {denying && (
        <DenyDialog
          requesterName={denying.person_name ?? denying.person_email}
          submitting={busyId === denying.id}
          onCancel={() => setDenying(null)}
          onConfirm={handleDenyConfirm}
        />
      )}
    </div>
  );
}
